import PropTypes from "prop-types";
import React from "react";
import {
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Select as MuiSelect
} from "@mui/material";
import { useField, useFormikContext } from "formik";
import { find } from "lodash";
import makeStyles from "@mui/styles/makeStyles";
import { Tooltip } from "../Tooltip";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  loadSpinner: {
    width: "100%",
    paddingRight: "42px"
  },
  biggerFontSize: {
    fontSize: 20,
    width: 250,
    color: theme.palette.primary.main
  }
}));

const Select = ({
  menuItems,
  label,
  renderValue,
  onChange,
  disableUnderline,
  hasEmptyItem,
  disabled,
  loading,
  submitOnChange,
  variant,
  fullWidth,
  autoWidth,
  tooltip,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const { t } = useTranslation();
  const { submitForm, isSubmitting, setSubmitting } = useFormikContext();
  const { error, touched } = meta;
  const classes = useStyles();

  const handleRenderValue = value => {
    if (renderValue) {
      return renderValue(value);
    } else {
      const selectedItem = value && find(menuItems, { value });
      return selectedItem ? selectedItem.label : "";
    }
  };

  const handleChange = value => {
    setTimeout(() => helpers.setTouched(true));
    if (onChange) {
      onChange(value);
    } else {
      helpers.setValue(value);
      if (submitOnChange) {
        submitForm();
        setSubmitting(false);
      }
    }
  };

  const selectField = (
    <FormControl
      fullWidth
      variant="filled"
      disabled={disabled || isSubmitting}
      data-qa="formik-field-select"
    >
      {label && <InputLabel error={error && touched}>{label}</InputLabel>}

      <MuiSelect
        inputProps={{
          name: field.name,
          id: field.name
        }}
        className={variant === "standard" ? classes.biggerFontSize : null}
        variant={variant}
        fullWidth={fullWidth}
        renderValue={handleRenderValue}
        error={error && touched}
        onChange={({ target: { value } }) => handleChange(value)}
        value={field.value}
        disableUnderline={disableUnderline}
        autoWidth={autoWidth}
        data-qa="formik-field-select-input"
        SelectDisplayProps={{
          "data-qa": "formik-field-select-btn"
        }}
        hiddenLabel={!label}
      >
        {loading ? (
          <MenuItem disabled>{`${t("loading")}...`}</MenuItem>
        ) : (
          menuItems &&
          menuItems.map((menuItem, index) => (
            <MenuItem
              value={menuItem.value}
              key={index}
              data-qa={`menu-item-${
                !!menuItem.value ? menuItem.value : "empty"
              }`}
            >
              {menuItem.label}
            </MenuItem>
          ))
        )}
        {hasEmptyItem && <MenuItem value="" style={{ display: "none" }} />}
      </MuiSelect>
      {!!error && touched && (
        <FormHelperText
          data-qa={`helper-text-${props?.name}`}
          error={error && touched}
        >
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );

  return !!tooltip ? (
    <Tooltip title={tooltip}>{selectField}</Tooltip>
  ) : (
    selectField
  );
};

export default Select;

Select.propTypes = {
  name: PropTypes.string,
  menuItems: PropTypes.array,
  autoWidth: PropTypes.bool,
  /**
   * Pass in a method for onChange if you don't want the form value
   * to be automatically updated when an option is selected
   */
  onChange: PropTypes.func,
  tooltip: PropTypes.node
};

Select.defaultProps = {
  variant: "filled",
  fullWidth: true,
  disableUnderline: true
};
